import { useRef, useState } from "react";

import { Button } from "@go-novus/ui";

import { LOGO_ACCEPT, LOGO_MAX_BYTES } from "#/features/settings";
import * as m from "#/paraglide/messages";

export interface LogoFieldProps {
  disabled: boolean;
  /** 当前该显示的那张：有待保存的新图就是它，否则是已保存的（PRD 4.1 预览反映草稿）。 */
  logoUrl: string | null;
  /** 选中的文件只进草稿，按下保存才上传；`null` 是移除。 */
  onChange: (file: File | null) => void;
}

/** 品牌 Logo。传坏了界面上还是原来那张（PRD 4.6）。 */
export function LogoField({ disabled, logoUrl, onChange }: LogoFieldProps) {
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  function pick(file: File | null): void {
    // 大小在本地先拦一道：等按了保存再被拒，商家白等一趟。
    if (file !== null && file.size > LOGO_MAX_BYTES) {
      setError(m.settings_logo_failure_too_large());
      return;
    }

    setError(null);
    onChange(file);
  }

  return (
    <div className="border-t border-[var(--border-surface-default)] p-5">
      <div className="text-label font-semibold">{m.settings_logo_label()}</div>
      <div className="mt-2 flex items-center gap-3.5">
        {logoUrl === null ? (
          <div
            aria-hidden
            /* 描边用中灰而不是 border token：那个在暗色下是 12% 白，
               空位会整个融进面板里，跟主题色预设踩的是同一个坑。 */
            className="size-12 flex-none rounded-[var(--radius)] border border-dashed border-[var(--content-fg-subtle)]"
          />
        ) : (
          <img
            alt={m.settings_logo_alt()}
            className="size-12 flex-none rounded-[var(--radius)] border border-[var(--border-surface-default)] object-cover"
            src={logoUrl}
          />
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={disabled}
            onClick={() => {
              input.current?.click();
            }}
            size="sm"
            type="button"
          >
            {logoUrl === null
              ? m.settings_logo_upload()
              : m.settings_logo_replace()}
          </Button>
          {logoUrl !== null && (
            <Button
              disabled={disabled}
              onClick={() => {
                pick(null);
              }}
              size="sm"
              type="button"
              variant="quiet"
            >
              {m.settings_logo_remove()}
            </Button>
          )}
        </div>
      </div>
      <input
        accept={LOGO_ACCEPT}
        // 控件本体是这个 input，按钮只是替它挨点击；名字给在这里。
        aria-label={m.settings_logo_file_label()}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          // 清掉值，同一个文件再选一次也还会触发 change。
          event.target.value = "";
          if (file !== null) pick(file);
        }}
        ref={input}
        type="file"
      />
      <div className="mt-1.75 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
        {m.settings_logo_hint()}
      </div>
      {error !== null && (
        <p
          className="m-0 mt-1.5 text-micro text-[var(--content-vibrant-error-default)]"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
